/* ==========================================================================
   THE BLUE FRAME ATELIER — Wishlist.
   A self-contained, localStorage-backed wishlist. No app/back-end required.
   Modular so it can later be swapped for a Shopify metafield / app sync:
   everything reads and writes through the BFWishlist facade below, and the
   UI listens to a single 'bf:wishlist:change' event.

   Public API (window.BFWishlist):
     .all()            -> [{id, handle, title, url, image, price}]
     .ids()            -> [Number]
     .has(id)          -> Boolean
     .count()          -> Number
     .add(item)        -> Boolean (added?)
     .remove(id)       -> Boolean (removed?)
     .toggle(item)     -> Boolean (now saved?)
     .clear()          -> void
   Emits 'bf:wishlist:change' on window after any mutation.
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_KEY = 'bf_wishlist_v1';
  var EVENT = 'bf:wishlist:change';

  /* ---- storage facade (swap this block to move off localStorage) ------- */
  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      /* private mode / quota — fail silently, wishlist just won't persist */
    }
  }

  function emit() {
    window.dispatchEvent(
      new CustomEvent(EVENT, { detail: { items: read(), count: read().length } })
    );
  }

  function normalizeId(id) {
    return parseInt(id, 10);
  }

  var BFWishlist = {
    all: function () {
      return read();
    },
    ids: function () {
      return read().map(function (i) {
        return normalizeId(i.id);
      });
    },
    has: function (id) {
      id = normalizeId(id);
      return read().some(function (i) {
        return normalizeId(i.id) === id;
      });
    },
    count: function () {
      return read().length;
    },
    add: function (item) {
      if (!item || item.id == null) return false;
      var items = read();
      var id = normalizeId(item.id);
      if (items.some(function (i) { return normalizeId(i.id) === id; })) return false;
      items.push({
        id: id,
        handle: item.handle || '',
        title: item.title || '',
        url: item.url || '',
        image: item.image || '',
        price: item.price || ''
      });
      write(items);
      emit();
      return true;
    },
    remove: function (id) {
      id = normalizeId(id);
      var items = read();
      var next = items.filter(function (i) {
        return normalizeId(i.id) !== id;
      });
      if (next.length === items.length) return false;
      write(next);
      emit();
      return true;
    },
    toggle: function (item) {
      if (!item || item.id == null) return false;
      if (this.has(item.id)) {
        this.remove(item.id);
        return false;
      }
      this.add(item);
      return true;
    },
    clear: function () {
      write([]);
      emit();
    }
  };

  window.BFWishlist = BFWishlist;

  /* ---- <bf-wishlist-button> -------------------------------------------
     A save-toggle button. Reads product data from its own dataset so it
     works identically on product cards and the product detail page.
     --------------------------------------------------------------------- */
  var HEART =
    '<svg class="bf-wish-btn__icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">' +
    '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>' +
    '</svg>';

  if ('customElements' in window && !customElements.get('bf-wishlist-button')) {
    class BFWishlistButton extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;

        this.button = this.querySelector('button');
        if (!this.button) return;

        this._onClick = this.onClick.bind(this);
        this._onChange = this.render.bind(this);
        this.button.addEventListener('click', this._onClick);
        window.addEventListener(EVENT, this._onChange);
        this.render();
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this._onClick);
        window.removeEventListener(EVENT, this._onChange);
      }

      item() {
        var d = this.dataset;
        return {
          id: d.productId,
          handle: d.productHandle,
          title: d.productTitle,
          url: d.productUrl,
          image: d.productImage,
          price: d.productPrice
        };
      }

      onClick(event) {
        event.preventDefault();
        event.stopPropagation();
        var nowSaved = BFWishlist.toggle(this.item());
        this.render();
        if (nowSaved) {
          this.classList.remove('is-animating');
          // force reflow so the animation restarts each time
          void this.offsetWidth;
          this.classList.add('is-animating');
        }
        // If a card lives on the wishlist page, removing should drop the card.
        var page = this.closest('[data-bf-wishlist-card]');
        if (page && !nowSaved) {
          if (page.parentNode) page.parentNode.removeChild(page);
          document.dispatchEvent(new CustomEvent('bf:wishlist:page-updated'));
        }
      }

      render() {
        if (!this.dataset.productId || !this.button) return;
        var saved = BFWishlist.has(this.dataset.productId);
        this.classList.toggle('is-saved', saved);
        this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
        var label = saved
          ? this.dataset.labelSaved || 'Saved to wishlist'
          : this.dataset.labelAdd || 'Add to wishlist';
        this.button.setAttribute('aria-label', label);
        this.button.setAttribute('title', label);
        var text = this.button.querySelector('.bf-wish-btn__text');
        if (text) text.textContent = saved ? this.dataset.textSaved || 'Saved' : this.dataset.textAdd || 'Save';
      }
    }

    customElements.define('bf-wishlist-button', BFWishlistButton);
  }

  /* ---- header counter -------------------------------------------------- */
  function updateCounters() {
    var count = BFWishlist.count();
    document.querySelectorAll('[data-bf-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-bf-wishlist-link]').forEach(function (el) {
      el.classList.toggle('has-items', count > 0);
    });
    // Total labels + "has items" chrome (count line, continue-shopping bar,
    // drawer footer) — document-wide, since some live outside the page mount.
    document.querySelectorAll('[data-bf-wishlist-total]').forEach(function (el) {
      el.textContent = count;
    });
    document.querySelectorAll('[data-bf-wishlist-haslist]').forEach(function (el) {
      el.hidden = count === 0;
    });
  }

  /* ---- wishlist page renderer ------------------------------------------ */
  function formatPrice(price) {
    return price || '';
  }

  function buildCard(item) {
    var card = document.createElement('div');
    card.className = 'bf-wish-card bf-reveal';
    card.setAttribute('data-bf-wishlist-card', '');
    card.setAttribute('data-product-id', item.id);

    var img = item.image
      ? '<img src="' + item.image + '" alt="' + escapeHtml(item.title) + '" loading="lazy" width="600" height="750" class="bf-wish-card__img">'
      : '<div class="bf-wish-card__img bf-wish-card__img--empty" aria-hidden="true"></div>';

    var price = item.price
      ? '<p class="bf-wish-card__price">' + escapeHtml(formatPrice(item.price)) + '</p>'
      : '';

    card.innerHTML =
      '<a class="bf-wish-card__media" href="' + (item.url || '#') + '">' + img + '</a>' +
      '<div class="bf-wish-card__body">' +
        '<a class="bf-wish-card__title" href="' + (item.url || '#') + '">' + escapeHtml(item.title) + '</a>' +
        price +
        '<div class="bf-wish-card__actions">' +
          '<a class="bf-btn bf-btn--solid bf-press bf-wish-card__view" href="' + (item.url || '#') + '">View piece</a>' +
          '<bf-wishlist-button class="bf-wish-btn bf-wish-btn--inline is-saved"' +
            ' data-product-id="' + item.id + '"' +
            ' data-product-handle="' + escapeAttr(item.handle) + '"' +
            ' data-product-title="' + escapeAttr(item.title) + '"' +
            ' data-product-url="' + escapeAttr(item.url) + '"' +
            ' data-product-image="' + escapeAttr(item.image) + '"' +
            ' data-product-price="' + escapeAttr(item.price) + '"' +
            ' data-text-add="Save" data-text-saved="Remove">' +
            '<button type="button" class="bf-wish-btn__btn">' + HEART +
              '<span class="bf-wish-btn__text">Remove</span>' +
            '</button>' +
          '</bf-wishlist-button>' +
        '</div>' +
      '</div>';
    return card;
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
  function escapeAttr(str) {
    return escapeHtml(str).replace(/"/g, '&quot;');
  }

  function renderPage() {
    // Render into EVERY wishlist mount point — the CMS wishlist page (if the
    // merchant created it) and/or the header drawer both use these hooks.
    var roots = document.querySelectorAll('[data-bf-wishlist-page]');
    if (!roots.length) return;

    // Newest-first — the most recently wishlisted piece leads ("Recently saved").
    var items = BFWishlist.all().slice().reverse();
    var isEmpty = items.length === 0;

    roots.forEach(function (root) {
      var grid = root.querySelector('[data-bf-wishlist-grid]');
      var empty = root.querySelector('[data-bf-wishlist-empty]');

      if (grid) {
        grid.innerHTML = '';
        items.forEach(function (item, i) {
          var card = buildCard(item);
          // Flag the freshest saves so the page can badge "Recently saved".
          if (i < 3) card.setAttribute('data-bf-wishlist-recent', '');
          grid.appendChild(card);
        });
      }

      if (empty) empty.hidden = !isEmpty;
      if (grid) grid.hidden = isEmpty;

      // Re-run brand reveal animation on the freshly-built cards.
      if (window.BFReveal && typeof window.BFReveal.init === 'function') {
        window.BFReveal.init(grid);
      } else if (grid) {
        grid.querySelectorAll('.bf-reveal').forEach(function (el) {
          el.classList.add('is-visible');
        });
      }
    });
  }

  /* ---- wishlist drawer (graceful fallback for the header icon) ----------
     The header wishlist icon opens this client-side drawer, so it is fully
     functional whether or not a /pages/wishlist CMS page exists. If the
     merchant later creates that page, its link still works too. ----------- */
  function getDrawer() {
    return document.querySelector('[data-bf-wishlist-drawer]');
  }

  // Element that had focus before the drawer opened — restored on close so
  // keyboard/AT users are returned to where they were (dialog pattern).
  var lastDrawerFocus = null;

  function drawerFocusables(drawer) {
    var panel = drawer.querySelector('[data-bf-wishlist-panel]') || drawer;
    var nodes = panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(nodes, function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function trapDrawerFocus(event) {
    var drawer = getDrawer();
    if (!drawer || !drawer.classList.contains('is-open') || event.key !== 'Tab') return;
    var focusables = drawerFocusables(drawer);
    var panel = drawer.querySelector('[data-bf-wishlist-panel]');
    if (!focusables.length) {
      event.preventDefault();
      if (panel) panel.focus();
      return;
    }
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = document.activeElement;
    // Focus outside the dialog (or on the panel itself, shift-tabbing) wraps.
    if (event.shiftKey && (active === first || active === panel || !drawer.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !drawer.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  function openDrawer() {
    var drawer = getDrawer();
    if (!drawer) return false;
    renderPage();
    lastDrawerFocus = document.activeElement;
    drawer.hidden = false;
    // next frame so the transition runs
    window.requestAnimationFrame(function () {
      drawer.classList.add('is-open');
    });
    document.documentElement.classList.add('bf-wishlist-drawer-open');
    var panel = drawer.querySelector('[data-bf-wishlist-panel]');
    if (panel) panel.focus();
    return true;
  }

  function closeDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('bf-wishlist-drawer-open');
    // wait for the transition before hiding
    window.setTimeout(function () {
      if (!drawer.classList.contains('is-open')) drawer.hidden = true;
    }, 320);
    // Return focus to the trigger (dialog pattern) if it is still in the DOM.
    if (lastDrawerFocus && document.contains(lastDrawerFocus) && typeof lastDrawerFocus.focus === 'function') {
      lastDrawerFocus.focus();
    }
    lastDrawerFocus = null;
  }

  function wireDrawer() {
    // Header/menu/footer wishlist links open the drawer instead of navigating.
    // Matches the tagged header links plus any link pointing at the wishlist
    // page or the '#wishlist' hash (used by footer/CMS links).
    var links = document.querySelectorAll(
      '[data-bf-wishlist-link], a[href$="/pages/wishlist"], a[href="#wishlist"]'
    );
    links.forEach(function (link) {
      if (link._bfWired) return;
      link._bfWired = true;
      link.addEventListener('click', function (event) {
        // Only intercept when a drawer exists to open.
        if (!getDrawer()) return;
        event.preventDefault();
        openDrawer();
      });
    });

    var drawer = getDrawer();
    if (drawer && !drawer._bfWired) {
      drawer._bfWired = true;
      drawer.addEventListener('click', function (event) {
        if (event.target.closest('[data-bf-wishlist-close]')) {
          event.preventDefault();
          closeDrawer();
        }
      });
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
        trapDrawerFocus(event);
      });
    }
  }

  /* ---- boot ------------------------------------------------------------ */
  function boot() {
    updateCounters();
    renderPage();
    wireDrawer();
  }

  window.addEventListener(EVENT, function () {
    updateCounters();
  });
  document.addEventListener('bf:wishlist:page-updated', function () {
    // A card removed itself in place; refresh empty-state visibility on every mount.
    document.querySelectorAll('[data-bf-wishlist-page]').forEach(function (root) {
      var grid = root.querySelector('[data-bf-wishlist-grid]');
      var empty = root.querySelector('[data-bf-wishlist-empty]');
      var isEmpty = !grid || grid.children.length === 0;
      if (empty) empty.hidden = !isEmpty;
      if (grid) grid.hidden = isEmpty;
    });
  });

  // Keep multiple tabs in sync.
  window.addEventListener('storage', function (e) {
    if (e.key === STORAGE_KEY) boot();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify Theme Editor: re-run when sections re-render.
  document.addEventListener('shopify:section:load', boot);
})();
